import type { JSX } from 'react';

import { ShareButton } from './MyPhotosPage';
import { fill, PhotoImage } from './PhotoImage';
import { useMyPhotos } from './queries';
import { strings } from './strings';

/**
 * §6 client `/me/photos/:photoId` — one photograph, and the share action.
 *
 * **The record comes out of the gallery, not out of a route of its own.** §5
 * has `GET /me/photos` and `POST /me/photos/{id}/url` and nothing between
 * them: there is no endpoint that returns one photograph's record by id, and
 * adding one would be a second place for rule 6's ownership check to be got
 * wrong. Selecting from the list the caller already owns cannot return
 * somebody else's picture, because the list cannot contain one.
 *
 * An id that is not in that list is "not found" here rather than a 404 from
 * the api — the same answer, reached without asking a question that would
 * tell a stranger whether the photograph exists.
 */
export function MyPhotoPage({ photoId }: { photoId: string }): JSX.Element {
  const photos = useMyPhotos();
  const photo = photos.data?.find((row) => row.id === photoId);

  if (photos.isPending) {
    return <p className="p-4">{strings.loading}</p>;
  }
  if (photos.isError) {
    return (
      <p role="alert" className="p-4" data-testid="my-photo-failed">
        {strings.failed}
      </p>
    );
  }
  if (photo === undefined) {
    return (
      <p role="alert" className="p-4" data-testid="my-photo-missing">
        {strings.myEmpty}
      </p>
    );
  }

  return (
    <section className="space-y-3 p-4" data-testid="my-photo">
      <h1 className="text-xl font-semibold">{strings.myTitle}</h1>
      <div className="overflow-hidden rounded">
        <PhotoImage photo={photo} own className="w-full" />
      </div>
      <p className="text-sm text-muted-foreground">
        {fill(strings.takenOn, { when: photo.taken_at.slice(0, 10) })}
      </p>
      {photo.deleted_at === null ? (
        <p className="text-sm text-muted-foreground" data-testid="kept-until">
          {fill(strings.keptUntil, { when: photo.retain_until })}
        </p>
      ) : null}
      <ShareButton photo={photo} />
    </section>
  );
}
